import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Mail } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-white dark:bg-zinc-950">
        <div className="max-w-md w-full text-center">
          <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
            check your inbox
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mb-4 text-zinc-950 dark:text-zinc-100">
            Email sent.
          </h2>
          <p className="text-sm text-zinc-500 leading-relaxed mb-8">
            If an account with <strong className="text-zinc-900 dark:text-zinc-200">{email}</strong> exists,
            you'll receive a password reset link shortly. Check your spam folder if it doesn't appear.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-sm font-medium text-zinc-900 dark:text-zinc-100 hover:text-zinc-500 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-white dark:bg-zinc-950">
      <div className="max-w-md w-full">
        <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
          password reset
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mb-2 text-zinc-950 dark:text-zinc-100">
          Forgot password?
        </h2>
        <p className="text-sm text-zinc-500 mb-10">
          Enter your email and we'll send you a reset link.{" "}
          <Link to="/login" className="text-black dark:text-white underline underline-offset-4">
            Back to sign in
          </Link>
        </p>

        <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.1em] mb-2 text-zinc-900 dark:text-zinc-100">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-base focus:outline-none focus:border-black dark:focus:border-white transition-colors bg-transparent text-zinc-950 dark:text-zinc-100"
              placeholder="you@company.com"
              data-testid="forgot-password-email-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-black dark:bg-white text-white dark:text-black py-3.5 text-sm font-medium hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="forgot-password-submit"
          >
            <Mail className="h-4 w-4" />
            {loading ? "Sending..." : "Send reset link"}
          </button>
        </form>
      </div>
    </div>
  );
}
